import { useCallback, useEffect, useRef, useState } from "react";
import { api, type Attachment } from "../lib/api";

export type PendingShot = Attachment & { preview: string };

type Props = {
  busy: boolean;
  disabled?: boolean;
  placeholder?: string;
  /**
   * Draft text to start from. Set when arriving from a deep link like
   * `/chat?ask=…`, which is how a volume meter or a log line hands its
   * question to the chat instead of dead-ending.
   */
  initialText?: string;
  onSend: (text: string, attachments: Attachment[]) => void | Promise<void>;
  onStop: () => void;
};

const MAX_SHOTS = 4;

export function Composer({ busy, disabled, placeholder, initialText, onSend, onStop }: Props) {
  const [text, setText] = useState(initialText ?? "");
  const [shots, setShots] = useState<PendingShot[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const ta = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const root = useRef<HTMLDivElement>(null);

  /** Grow with content up to a ceiling, then scroll internally. */
  const autosize = useCallback(() => {
    const el = ta.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 168)}px`;
  }, []);

  useEffect(autosize, [text, autosize]);

  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const write = () => {
      document.documentElement.style.setProperty(
        "--composer-h",
        `${Math.round(el.getBoundingClientRect().height)}px`,
      );
    };
    write();
    const ro = new ResizeObserver(write);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--composer-h");
    };
  }, []);

  /*
   * A new ?ask= replaces the draft, but only when there is one and the box is
   * still empty-ish — never clobber something half-typed.
   */
  useEffect(() => {
    if (!initialText) return;
    setText((cur) => (cur ? cur : initialText));
    ta.current?.focus();
  }, [initialText]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(""), 4000);
    return () => clearTimeout(t);
  }, [error]);

  const addFiles = useCallback(
    async (files: File[]) => {
      const images = files.filter((f) => f.type.startsWith("image/"));
      if (!images.length) return;
      const room = MAX_SHOTS - shots.length;
      if (room <= 0) {
        setError(`Up to ${MAX_SHOTS} images per message`);
        return;
      }
      setUploading(true);
      setError("");
      try {
        // Upload immediately as multipart. The old build base64'd images into
        // the chat POST, so a 4-screenshot message became a 12 MB JSON body.
        const { attachments } = await api.upload(images.slice(0, room));
        setShots((prev) => [
          ...prev,
          ...attachments.map((a, i) => ({
            ...a,
            preview: URL.createObjectURL(images[i]!),
          })),
        ]);
      } catch (e) {
        setError(e instanceof Error ? e.message : "upload failed");
      } finally {
        setUploading(false);
      }
    },
    [shots.length],
  );

  const removeShot = (path: string) =>
    setShots((prev) => {
      const gone = prev.find((s) => s.path === path);
      if (gone) URL.revokeObjectURL(gone.preview);
      return prev.filter((s) => s.path !== path);
    });

  const submit = async () => {
    const body = text.trim();
    if ((!body && !shots.length) || busy || disabled) return;
    const attachments = shots.map(({ preview: _preview, ...a }) => a);
    setText("");
    shots.forEach((s) => URL.revokeObjectURL(s.preview));
    setShots([]);
    // Restore the height immediately so the thread does not jump.
    requestAnimationFrame(autosize);
    try {
      await onSend(body, attachments);
    } catch (e) {
      // Put the text back so a failed send never loses what was typed.
      setText(body);
      setError(e instanceof Error ? e.message : "send failed");
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends on a physical keyboard; on touch it inserts a newline and
    // the Send button is the only submit, matching every native chat app.
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    if (e.key === "Enter" && !e.shiftKey && !isTouch) {
      e.preventDefault();
      void submit();
    }
  };

  const onPaste = (e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData?.files ?? []).filter((f) =>
      f.type.startsWith("image/"),
    );
    if (files.length) {
      e.preventDefault();
      void addFiles(files);
    }
  };

  const canSend = (text.trim().length > 0 || shots.length > 0) && !uploading;

  return (
    <div className="composer" ref={root}>
      {error && <div className="composer__error">{error}</div>}

      {shots.length > 0 && (
        <div className="composer__shots">
          {shots.map((s) => (
            <div key={s.path} className="shot">
              <img src={s.preview} alt={s.name} />
              <button
                type="button"
                className="shot__x"
                onClick={() => removeShot(s.path)}
                aria-label={`Remove ${s.name}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="composer__row">
        <button
          type="button"
          className="btn btn--ghost btn--icon composer__attach"
          onClick={() => fileInput.current?.click()}
          disabled={disabled || uploading}
          aria-label="Attach image"
        >
          {uploading ? <span className="spinner" /> : <PlusIcon />}
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            void addFiles(Array.from(e.target.files ?? []));
            e.target.value = "";
          }}
        />

        <textarea
          ref={ta}
          className="composer__input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          rows={1}
          placeholder={placeholder ?? "Message…"}
          disabled={disabled}
          enterKeyHint="enter"
          autoCapitalize="sentences"
          autoCorrect="on"
          spellCheck
        />

        {busy ? (
          <button type="button" className="btn btn--icon composer__stop" onClick={onStop} aria-label="Stop">
            <StopIcon />
          </button>
        ) : (
          <button
            type="button"
            className="btn btn--primary btn--icon composer__send"
            onClick={() => void submit()}
            disabled={!canSend || disabled}
            aria-label="Send"
          >
            <SendIcon />
          </button>
        )}
      </div>
    </div>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}

function StopIcon() {
  // Filled square on purpose: a stop control has to read as solid against
  // the gradient send button it replaces. An outline rect looks empty.
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor">
      <rect x="6" y="6" width="12" height="12" rx="2.5" />
    </svg>
  );
}
